import { useRef } from 'react';
import { ApiResponseError } from '../../../../types/errors';
import { CUSTOMER_LOOKUP, useGetCustomer } from '../../../customers/hooks/use-get-customer';
import { useStoredCustomerEmail } from './use-stored-customer-email';

// Outlasts the by-email lookup's post-create 404 window (it's eventually consistent) without
// suppressing refetches long enough to miss a later deletion.
const TRACKED_CUSTOMER_STALE_TIME = 10_000;

// The tracked customer is the one we created, remembered by email. Point lookup, not search: it
// resolves a fresh create immediately and never infers an account the owner can't sign in as.
//
// Exposes the full query (aliasing `data` to `customer`) so callers keep the domain name. Gate on
// `isLoading` (true only on the first fetch), not `isPending` (which a disabled no-email query
// reports as `true` indefinitely, and which would flash steps back to pending on refocus refetches).
export function useTrackedCustomer() {
  const { email } = useStoredCustomerEmail();

  const lookup = useGetCustomer(CUSTOMER_LOOKUP.email(email ?? ''), {
    retry: (count, error) => {
      // 404 = deleted; don't retry, fall through to "none".
      if (error instanceof ApiResponseError && error.status === 404) {
        return false;
      }
      return count < 3;
    },
    enabled: !!email,
    staleTime: TRACKED_CUSTOMER_STALE_TIME,
  });

  // Latch loading to the first settle. The query re-fetches cold on remount; re-arming `isLoading`
  // would flash every consuming step (this one, order) back to skeleton.
  const hasSettled = useRef(false);
  if (!lookup.isLoading) hasSettled.current = true;

  return { ...lookup, customer: lookup.data, isLoading: !hasSettled.current && lookup.isLoading };
}
